/**
 * Spaced repetition algorithm based on SM-2
 * Manages character selection and review scheduling
 */

// Canonical SM-2 ease factor bounds
export const MIN_EASE_FACTOR = 1.3;
export const MAX_EASE_FACTOR = 2.5;

const SECONDS_PER_DAY = 86400;

/**
 * Calculate the next interval and ease factor for spaced repetition
 * @param {number} repetitions - Number of consecutive correct answers
 * @param {number} easeFactor - Current ease factor
 * @param {boolean} correct - Whether the answer was correct
 * @returns {{repetitions: number, easeFactor: number}} Updated values
 */
export function calculateNextInterval(repetitions, easeFactor, correct) {
    // If incorrect, reset repetitions
    if (!correct) {
        return {
            repetitions: 0,
            easeFactor: Math.max(MIN_EASE_FACTOR, easeFactor - 0.2)
        };
    }

    // If correct, increase repetitions and nudge the ease factor up,
    // clamped to the canonical SM-2 range
    return {
        repetitions: repetitions + 1,
        easeFactor: Math.min(MAX_EASE_FACTOR, Math.max(MIN_EASE_FACTOR, easeFactor + 0.1))
    };
}

/**
 * Calculate the next review time based on SM-2 algorithm
 * @param {number} repetitions - Number of consecutive correct answers
 * @param {number} easeFactor - Current ease factor
 * @returns {number} Interval in seconds until next review
 */
export function calculateReviewInterval(repetitions, easeFactor) {
    if (repetitions <= 0) return 0; // Review immediately
    if (repetitions === 1) return SECONDS_PER_DAY; // 1 day
    if (repetitions === 2) return 6 * SECONDS_PER_DAY; // 6 days

    // Canonical SM-2: each further interval multiplies the previous by the ease factor
    return Math.round(6 * Math.pow(easeFactor, repetitions - 2) * SECONDS_PER_DAY);
}

/**
 * Generate a practice queue based on character statistics
 * @param {Object} charStats - Character statistics object
 * @param {string[]} availableChars - Array of available characters
 * @param {number} queueSize - Desired queue size
 * @param {string|null} previousChar - Character that just played, kept out of position 0
 * @returns {string[]} Array of characters to practice
 */
export function generatePracticeQueue(charStats, availableChars, queueSize = 10, previousChar = null) {
    const now = Date.now();

    // Check if this is the first session (no character has been practiced)
    const isFirstSession = availableChars.every(char => {
        const stats = charStats[char];
        return stats && stats.correct === 0 && stats.incorrect === 0;
    });

    // If first session, return a completely random selection
    if (isFirstSession) {
        return shuffleArray(availableChars).slice(0, queueSize);
    }

    // Get characters that are due for review
    const dueChars = availableChars.filter(char =>
        charStats[char] && charStats[char].nextReview <= now
    );

    // Sort by difficulty (more incorrect = higher priority)
    const sortedChars = dueChars.sort((a, b) => {
        const aDifficulty = (charStats[a].incorrect || 0) - (charStats[a].correct || 0);
        const bDifficulty = (charStats[b].incorrect || 0) - (charStats[b].correct || 0);
        return bDifficulty - aDifficulty;
    });

    // Up to 70% of the queue is due characters, most difficult first
    const priorityChars = sortedChars.slice(0, Math.ceil(queueSize * 0.7));

    // Fill the remainder randomly to avoid overfitting
    const remainingChars = availableChars.filter(char => !priorityChars.includes(char));
    const randomChars = shuffleArray(remainingChars).slice(0, queueSize - priorityChars.length);

    // Combine and shuffle
    const queue = shuffleArray([...priorityChars, ...randomChars]);

    // Don't play the same character back to back across queue boundaries
    if (previousChar && queue.length > 1 && queue[0] === previousChar) {
        [queue[0], queue[1]] = [queue[1], queue[0]];
    }

    return queue;
}

/**
 * Fisher-Yates shuffle algorithm
 * @param {Array} array - Array to shuffle
 * @returns {Array} Shuffled copy of the array
 */
export function shuffleArray(array) {
    const shuffled = Array.from(array);
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

/**
 * Initialize character statistics for all characters
 * @param {string[]} characters - Array of characters to initialize
 * @returns {Object} Character statistics object
 */
export function initializeCharStats(characters) {
    const stats = {};

    characters.forEach(char => {
        stats[char] = {
            correct: 0,
            incorrect: 0,
            repetitions: 0,
            easeFactor: 2.5,
            nextReview: Date.now(),
            lastPracticed: null
        };
    });

    return stats;
}

/**
 * Update character statistics after an attempt
 * @param {Object} charStats - Current character statistics
 * @param {string} char - Character that was attempted
 * @param {boolean} correct - Whether the attempt was correct
 * @returns {Object} Updated character statistics
 */
export function updateCharacterStats(charStats, char, correct) {
    const stat = charStats[char];
    if (!stat) return charStats;

    // Calculate next interval using SM-2
    const result = calculateNextInterval(
        stat.repetitions,
        stat.easeFactor,
        correct
    );

    // Calculate next review time
    const interval = calculateReviewInterval(result.repetitions, result.easeFactor);
    const nextReview = Date.now() + (interval * 1000);

    // Update stats
    return {
        ...charStats,
        [char]: {
            ...stat,
            correct: stat.correct + (correct ? 1 : 0),
            incorrect: stat.incorrect + (correct ? 0 : 1),
            repetitions: result.repetitions,
            easeFactor: result.easeFactor,
            nextReview: nextReview,
            lastPracticed: Date.now()
        }
    };
}

/**
 * Revert a character's most recent successful update, re-scoring it as incorrect.
 * Used when the learner marks the previous character wrong one beat late.
 * @param {Object} charStats - Current character statistics
 * @param {string} char - Character to revert
 * @param {Object} snapshot - The character's stats captured before the update
 * @returns {Object} Updated character statistics
 */
export function revertToIncorrect(charStats, char, snapshot) {
    if (!charStats[char] || !snapshot) return charStats;

    // Re-run SM-2 from the pre-update state with an incorrect answer
    const result = calculateNextInterval(snapshot.repetitions, snapshot.easeFactor, false);
    const interval = calculateReviewInterval(result.repetitions, result.easeFactor);

    return {
        ...charStats,
        [char]: {
            ...snapshot,
            incorrect: snapshot.incorrect + 1,
            repetitions: result.repetitions,
            easeFactor: result.easeFactor,
            nextReview: Date.now() + (interval * 1000),
            lastPracticed: Date.now()
        }
    };
}

/**
 * Get character mastery level based on statistics
 * @param {Object} charStat - Character statistics
 * @returns {string} Mastery level: 'unpracticed', 'struggling', 'learning', 'good', 'mastered'
 */
export function getCharacterMastery(charStat) {
    const total = charStat.correct + charStat.incorrect;

    if (total === 0) return 'unpracticed';

    const accuracy = (charStat.correct / total) * 100;

    if (accuracy >= 90 && total >= 3) return 'mastered';
    if (accuracy >= 80) return 'good';
    if (accuracy >= 60) return 'learning';
    return 'struggling';
}

/**
 * Calculate overall accuracy from character statistics
 * @param {Object} charStats - Character statistics object
 * @returns {{accuracy: number, totalAttempts: number, totalCorrect: number}}
 */
export function calculateOverallAccuracy(charStats) {
    let totalCorrect = 0;
    let totalAttempts = 0;

    Object.values(charStats).forEach(stat => {
        totalCorrect += stat.correct;
        totalAttempts += stat.correct + stat.incorrect;
    });

    const accuracy = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;

    return {
        accuracy,
        totalAttempts,
        totalCorrect
    };
}